import { deleteUserDevice, getUserDevices, postUserDevice } from '@/utils/api/userDevices'
import { Platform } from 'quasar'
import { devLog } from '@/utils/index'
import { $ui } from '@/utils/ui'

const { cordova } = window
const { messaging } = Platform.is.cordova
  ? {}
  : require('boot/firebase')

/**
 * Factory pour handle les messages et uniformiser le jeu de donnée
 * @param handler
 * @return {function(...[*]=)}
 * @private
 */
const _onMessage = (handler) => (payload) => {
  const {
    messageType,
    tap,
    from,
    title,
    body,
    // eslint-disable-next-line camelcase
    collapse_key,
    ...data
  } = payload
  devLog('Notification', payload)
  if (messageType || tap) {
    handler({
      from,
      collapse_key,
      messageType,
      tap,
      data,
      notification: {
        title,
        body
      }
    })
  } else {
    handler(payload)
  }
}

const _onTokenRefresh = async () => {
  devLog('FCM tokenRefresh')
  const token = await getToken()
  const { devices } = await getUserDevices()
  if (devices.length && devices.some((d) => d.token === token)) {
    return true
  }
  const deviceInfos = Platform.is.cordova
    ? {
      deviceType: 'mobile',
      deviceVendor: device.manufacturer,
      deviceModel: device.model,
      osName: device.platform,
      osVersion: device.version
    }
    : {
      deviceType: Platform.is.destop ? 'desktop' : 'mobile',
      osName: Platform.userAgent.platform
    }
  await postUserDevice({
    token,
    deviceInfos,
    browserName: Platform.userAgent.name,
    browserVersion: Platform.userAgent.version
  })
}

/**
 * Gestion de l'évent onMessage de fireBase
 * @return {Unsubscribe | firebase.Unsubscribe | Promise<void> | void}
 */
export const onMessage = async (cb) => {
  if ($ui.notificationStatus) {
    try {
      await getToken()
      return window.FirebasePlugin
        ? window.FirebasePlugin.onMessageReceived(_onMessage(cb))
        : messaging.onMessage(_onMessage(cb))
    } catch (e) {
      devLog('error', e)
    }
  }
  // mess(function )
}

/**
 * Check la regsitration du device pour les notifications
 * @return {Promise<{status: boolean}>}
 */
export const checkDeviceRegistration = async () => {
  const firebase = window.FirebasePlugin
    ? window.FirebasePlugin
    : messaging
  try {
    await _onTokenRefresh()
    firebase.onTokenRefresh(_onTokenRefresh)
    // mess(function )
  } catch (e) {
    devLog('error', e)
    return {
      status: false,
      error: e
    }
  }
  return {
    status: true
  }
}

/**
 * Retourne le token FCM sous forme de Promise
 * @return {*}
 */
export const getToken = () => window.FirebasePlugin
  ? new Promise((resolve, reject) =>
    window.FirebasePlugin.getToken((token) => {
      token
        ? resolve(token)
        : reject(`Token is null`)
    }, reject)
  )
  : messaging.getToken()

/**
 * Supprimer la souscription push
 * @return {Promise<void>}
 */
export const unregister = async () => {
  try {
    const token = await getToken()
    if (token) {
      await (
        window.FirebasePlugin
          ? window.FirebasePlugin.unregister()
          : messaging.deleteToken(token)
      )
      await deleteUserDevice(token)
    }
  } catch (e) { }
}

let notificationId = 10 // On réserve 1 id
export const createNotificationStack = (summary) => {
  const { notification } = cordova.plugins
  const stack = [
    {
      id: notificationId++,
      ...summary
    }
  ]
  return {
    notify: (notif) => {
      stack.push({ id: notificationId++, ...notif })
      notification.local.schedule(stack)
    },
    clear: () => {
      stack.splice(0, 1)
      notification.local.clearAll(stack)
    }
  }
}

export const requestLocalNotificationsPermission = async () => {
  if (!cordova) {
    try {
      const permission = await window.Notification.requestPermission()
      if (permission !== 'denied') {
        return true
      }
    } catch (e) {
      devLog('error', e)
    }
    throw new Error(`Impossible d'activer les notifications. Veuillez autoriser les notifications de votre navigateur.`)
  }
  const { notification } = cordova.plugins
  try {
    return new Promise((resolve, reject) => {
      notification.local.requestPermission((granted) => {
        devLog('info', 'notification.local.requestPermission', granted)
        granted
          ? resolve(granted)
          : reject(new Error(`Impossible d'activer les notifications. Veuillez autoriser les notifications dans les paramètres de l'application.`))
      })
    })
  } catch (e) {
    devLog(e)
  }
  throw new Error(`Impossible d'activer les notifications. Veuillez autoriser les notifications dans les paramètres de l'application.`)
}
